import { Link } from '@tanstack/react-router';
import type { ArticlePendingItemDto } from '@zoxo/shared';

import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/ui/empty-state';
import { formatDate } from './format';
import { usePendingArticles, useSetArticleActive } from './use-admin-articles';
import { Inbox } from '@/components/icons';

/**
 * Admin approval queue (V4 §C) — minimal operational UI. Lists pending
 * (isActive=false) articles; each row links to the (author/admin-visible) detail
 * and offers "승인" (publish) / "숨김" (keep hidden). Admin role is enforced at
 * the route (non-admin can't reach here) AND by the server (403). A fuller
 * dashboard is a follow-up.
 */
export function AdminArticleList() {
  const { data, isPending, isError, error, refetch } = usePendingArticles();

  if (isPending) {
    return <p className="text-sm text-text-muted">불러오는 중…</p>;
  }

  if (isError) {
    return (
      <div role="alert" className="py-8 text-center">
        <p className="mb-4 text-sm text-text">
          승인 대기 목록을 불러오지 못했어요.
        </p>
        <Button variant="secondary" onClick={() => void refetch()}>
          다시 시도
        </Button>
        <span hidden>{error.message}</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <EmptyState icon={<Inbox />} title="승인 대기 중인 기사가 없어요." />
    );
  }

  return (
    <ul className="divide-y divide-border-subtle">
      {data.map((article) => (
        <PendingRow key={article.id} article={article} />
      ))}
    </ul>
  );
}

function PendingRow({ article }: { article: ArticlePendingItemDto }) {
  const setActive = useSetArticleActive(article.id);

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4">
      <div className="min-w-0 flex-[1_1_100%]">
        <Link
          to="/articles/$id"
          params={{ id: article.id }}
          className="mb-1 block font-bold text-text hover:text-[#18BB69]"
        >
          {article.title}
        </Link>
        <div className="flex flex-wrap gap-1.5 text-xs text-text-subtle">
          {/* 닉네임만 — 실명·식별자 없음. */}
          <span>{article.author.nickname}</span>
          <span aria-hidden="true">·</span>
          <span>{formatDate(article.createdAt)}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {/* Both row actions stay secondary (no per-row full-tone primary) so the
            list keeps a single primary at the page level. 승인/숨김 are told
            apart by label, not by weight. */}
        <Button
          variant="outline"
          size="sm"
          className="border-primary text-primary"
          disabled={setActive.isPending}
          onClick={() => setActive.mutate({ isActive: true })}
        >
          {setActive.isPending ? '처리 중…' : '승인'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={setActive.isPending}
          onClick={() => setActive.mutate({ isActive: false })}
        >
          숨김
        </Button>
      </div>
      {setActive.isError && (
        <p className="flex-[1_1_100%] text-sm text-danger" role="alert">
          처리에 실패했어요. 잠시 후 다시 시도해 주세요.
        </p>
      )}
    </li>
  );
}
